import { useForm } from 'react-hook-form';
import { useFormTracking } from '@/shared/hooks/useFormTracking';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { getApexCountryCode, getDefaultCountryCode } from '@/shared/localization';
import OAuthButtons from '../oauth/OAuthButtons';
import AuthFormHeading from '../common/components/AuthFormHeading';
import AuthSwitchLink from '../common/components/AuthSwitchLink';
import FormBaseError from '../common/components/FormBaseError';
import OAuthDivider from '../oauth/OAuthDivider';
import EmailField from '../common/fields/EmailField';
import type { OAuthProvider } from '../oauth/oauth';
import type { ServerErrors } from '@/shared/utils/fieldErrors';
import PhoneField from '../common/fields/PhoneField';
import NameField from '../common/fields/NameField';
import CountryField from '../common/fields/CountryField';
import TermsCheckbox from '../common/fields/TermsCheckbox';
import PasswordFields from '../common/fields/PasswordFields';
import SubmitButton from '../common/fields/SubmitButton';

const DRAFT_FIELDS = ['name', 'email', 'country', 'phone'] as const;

export interface SignUpFormValues {
  name: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  country: string;
  phone: string;
  termsAccepted: boolean;
}

interface Props {
  serverErrors: ServerErrors;
  oauthError: string | null;
  isLoading: boolean;
  onSubmit: (values: SignUpFormValues) => void;
  onOAuth: (provider: OAuthProvider) => void;
  onSwitchToSignIn: () => void;
}

export default function SignUpForm({
  serverErrors,
  oauthError,
  isLoading,
  onSubmit,
  onOAuth,
  onSwitchToSignIn,
}: Props) {
  const countryFromDomain = getApexCountryCode();
  const defaultCountry = countryFromDomain ?? getDefaultCountryCode() ?? '';
  const {
    register,
    control,
    trigger,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      country: defaultCountry,
      phone: '',
      termsAccepted: false,
    },
  });

  const draft = useFormDraft<SignUpFormValues>({
    formType: 'sign_up',
    fields: DRAFT_FIELDS,
    watch,
    setValue,
  });

  const { formProps, wrapSubmit, onInvalid } = useFormTracking<SignUpFormValues>({
    formType: 'sign_up',
    clarityStage: 'sign_up_page',
    clarityStartedStage: 'sign_up_started',
    startEvent: 'sign_up_start',
    getValues,
    errors,
  });

  const submitAndDiscardDraft = (values: SignUpFormValues) => {
    draft.discard();
    onSubmit(values);
  };

  return (
    <div className="space-y-6">
      <AuthFormHeading headingId="auth.signUp.heading" descriptionId="auth.signUp.description" />

      <FormBaseError messages={serverErrors.base} />

      <OAuthButtons onOAuth={onOAuth} error={oauthError} />

      <OAuthDivider />

      <form className="space-y-5" onSubmit={handleSubmit(wrapSubmit(submitAndDiscardDraft), onInvalid)} {...formProps}>
        <NameField register={register} errors={errors} serverErrors={serverErrors} />

        <CountryField
          register={register}
          errors={errors}
          serverErrors={serverErrors}
          answeredByDomain={Boolean(countryFromDomain)}
          defaultCountry={defaultCountry}
        />

        <EmailField register={register} errors={errors} serverErrors={serverErrors} />

        <PhoneField control={control} trigger={trigger} errors={errors} serverErrors={serverErrors} />

        <PasswordFields register={register} errors={errors} serverErrors={serverErrors} />

        <TermsCheckbox register={register} errors={errors} serverErrors={serverErrors} />

        <SubmitButton isLoading={isLoading} submitId="auth.signUp.submit" submittingId="auth.signUp.submitting" />
      </form>

      <AuthSwitchLink labelId="auth.signUp.switchToSignIn" onClick={onSwitchToSignIn} />
    </div>
  );
}
